import { useMemo, useState, type ReactNode } from "react";

export interface Column<T> {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  width?: string | number;
  render: (row: T) => ReactNode;
  /** Enables header sorting when provided. */
  sortValue?: (row: T) => string | number | null | undefined;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchText?: (row: T) => string;
  pageSize?: number;
  loading?: boolean;
  emptyText?: string;
  initialSort?: { key: string; dir: "asc" | "desc" };
  toolbar?: ReactNode;
  onRowClick?: (row: T) => void;
  dense?: boolean;
  maxHeight?: number | string;
  footerNote?: string;
}

function compare(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "ko");
}

/**
 * Client-side table with search, sort, and pagination.
 *
 * Control Tower tables are bounded (<= 500 rows), so client-side handling is
 * sufficient and keeps the API simple. Columns own their cell rendering;
 * sorting only activates when `sortValue` is provided.
 */
export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  searchable = false,
  searchPlaceholder = "검색...",
  searchText,
  pageSize,
  loading = false,
  emptyText = "데이터가 없습니다.",
  initialSort,
  toolbar,
  onRowClick,
  dense = false,
  maxHeight,
  footerNote,
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(initialSort ?? null);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim() || !searchText) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((row) => searchText(row).toLowerCase().includes(q));
  }, [rows, query, searchable, searchText]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return filtered;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => factor * compare(column.sortValue!(a), column.sortValue!(b)));
  }, [filtered, sort, columns]);

  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  const visible = pageSize ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted;

  const toggleSort = (column: Column<T>) => {
    if (!column.sortValue) return;
    setPage(0);
    setSort((prev) => {
      if (prev?.key !== column.key) return { key: column.key, dir: "desc" };
      if (prev.dir === "desc") return { key: column.key, dir: "asc" };
      return null;
    });
  };

  return (
    <div className="ui-table-wrap">
      {(searchable || toolbar) && (
        <div className="ui-toolbar">
          {searchable && (
            <input
              className="ui-search"
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
            />
          )}
          <div className="ui-toolbar-extra">{toolbar}</div>
          <span className="ui-count">
            {filtered.length.toLocaleString()}건
            {query && filtered.length !== rows.length ? ` / 전체 ${rows.length.toLocaleString()}` : ""}
          </span>
        </div>
      )}

      <div className="ui-table-scroll" style={maxHeight ? { maxHeight } : undefined}>
        <table className={dense ? "ui-table dense" : "ui-table"}>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    style={{ width: column.width, textAlign: column.align ?? "left" }}
                    className={column.sortValue ? "ui-th-sortable" : undefined}
                    onClick={() => toggleSort(column)}
                    aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}
                  >
                    {column.header}
                    {column.sortValue ? (
                      <span className={`ui-sort ${active ? "active" : ""}`}>
                        {active && sort!.dir === "asc" ? "▲" : "▼"}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: Math.min(pageSize ?? 5, 8) }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      <span className="ui-skeleton" />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="ui-table-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? "ui-row-clickable" : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{ textAlign: column.align ?? "left" }}
                      className={column.className}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(pageSize && sorted.length > pageSize) || footerNote ? (
        <div className="ui-table-footer">
          {footerNote ? <span className="muted-note">{footerNote}</span> : <span />}
          {pageSize && sorted.length > pageSize ? (
            <div className="ui-pagination">
              <button
                type="button"
                className="ui-page-btn"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                이전
              </button>
              <span className="ui-page-info">
                {safePage + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="ui-page-btn"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                다음
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
